"use client";

import React, { useState, useEffect } from "react";
import { Button, Card, CardBody, Col, Container, Row } from "reactstrap";
import { toast } from "react-toastify";
import Api from "../../api/Api";

//Import Breadcrumb
import Breadcrumbs from "../../components/Common/Breadcrumb";
import ServiceOrderTableItem from "./ServiceOrderTableItem";
import ServiceOrderDetailsModal from "./ServiceOrderDetailsModal";

const ServiceOrders = () => {
  const [modal_center, setmodal_center] = useState(false);
  const [selectedServiceOrder, setSelectedServiceOrder] = useState({});
  const [serviceOrders, setServiceOrders] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [showFiltersModal, setShowFiltersModal] = useState(false);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [minTotal, setMinTotal] = useState("");
  const [maxTotal, setMaxTotal] = useState("");

  // Pagination states
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  document.title = "Service Orders | Tafaseel Admin";

  const statusOptions = [
    { label: "All Statuses", value: "" },
    { label: "Pending", value: "pending" },
    { label: "Accepted", value: "accepted" },
    { label: "In Progress", value: "in_progress" },
    { label: "Completed", value: "completed" },
    { label: "Cancelled", value: "cancelled" },
    { label: "Refunded", value: "refunded" },
  ];

  useEffect(() => {
    fetchServiceOrders();
  }, [
    currentPage,
    searchQuery,
    statusFilter,
    dateFrom,
    dateTo,
    minTotal,
    maxTotal,
  ]);

  // Fetch service orders from API
  const fetchServiceOrders = async () => {
    try {
      setLoading(true);

      const params = {
        page: currentPage,
        per_page: itemsPerPage,
        search: searchQuery,
        status: statusFilter,
        date_from: dateFrom,
        date_to: dateTo,
        min_total: minTotal,
        max_total: maxTotal,
      };

      // Remove empty parameters
      Object.keys(params).forEach((key) => {
        if (
          params[key] === "" ||
          params[key] === null ||
          params[key] === undefined
        ) {
          delete params[key];
        }
      });

      const token = localStorage.getItem("token");
      const response = await Api.get("/admin/service-orders", {
        params,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.success) {
        setServiceOrders(response.data.data);
        setFilteredData([]);
      } else {
        toast.error(response.data.message || "Failed to fetch service orders", {
          position: "bottom-center",
          autoClose: 3000,
          theme: "colored",
        });
      }
    } catch (error) {
      console.error("Error fetching service orders:", error);
      toast.error("Network error. Please check your connection.", {
        position: "bottom-center",
        autoClose: 3000,
        theme: "colored",
      });
    } finally {
      setLoading(false);
    }
  };

  function tog_center() {
    setmodal_center(!modal_center);
    setSelectedServiceOrder({});
  }

  const handleShow = async (serviceOrder) => {
    try {
      const token = localStorage.getItem("token");
      const response = await Api.get(
        `/admin/service-orders/${serviceOrder.id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success) {
        setSelectedServiceOrder(response.data.data);
        setmodal_center(true);
      } else {
        toast.error(
          response.data.message || "Failed to fetch service order details",
          {
            position: "bottom-center",
            autoClose: 3000,
            theme: "colored",
          }
        );
      }
    } catch (error) {
      console.error("Error fetching service order details:", error);
      toast.error("Network error. Please try again.", {
        position: "bottom-center",
        autoClose: 3000,
        theme: "colored",
      });
    }
  };

  const handleDelete = async (serviceOrderId) => {
    if (
      !window.confirm(
        "Are you sure you want to delete this service order? This action cannot be undone."
      )
    ) {
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const response = await Api.delete(
        `/admin/service-orders/${serviceOrderId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success) {
        toast.success("Service order deleted successfully", {
          position: "bottom-center",
          autoClose: 3000,
          theme: "colored",
        });

        // Remove service order from local state
        setServiceOrders(
          serviceOrders.filter((order) => order.id !== serviceOrderId)
        );
        setFilteredData(
          filteredData.filter((order) => order.id !== serviceOrderId)
        );
      } else {
        toast.error(response.data.message || "Failed to delete service order", {
          position: "bottom-center",
          autoClose: 3000,
          theme: "colored",
        });
      }
    } catch (error) {
      console.error("Error deleting service order:", error);
      toast.error("Network error. Please try again.", {
        position: "bottom-center",
        autoClose: 3000,
        theme: "colored",
      });
    }
  };

  const handleUpdateServiceOrderStatus = async (serviceOrderId, newStatus) => {
    try {
      const token = localStorage.getItem("token");
      const response = await Api.put(
        `/admin/service-orders/${serviceOrderId}/status`,
        {
          status: newStatus,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success) {
        toast.success("Service order status updated successfully", {
          position: "bottom-center",
          autoClose: 3000,
          theme: "colored",
        });

        // Update service order status in local state
        setServiceOrders(
          serviceOrders.map((order) =>
            order.id === serviceOrderId
              ? { ...order, status: newStatus }
              : order
          )
        );

        if (filteredData.length > 0) {
          setFilteredData(
            filteredData.map((order) =>
              order.id === serviceOrderId
                ? { ...order, status: newStatus }
                : order
            )
          );
        }

        if (
          selectedServiceOrder &&
          selectedServiceOrder.id === serviceOrderId
        ) {
          setSelectedServiceOrder({
            ...selectedServiceOrder,
            status: newStatus,
          });
        }
      } else {
        toast.error(
          response.data.message || "Failed to update service order status",
          {
            position: "bottom-center",
            autoClose: 3000,
            theme: "colored",
          }
        );
      }
    } catch (error) {
      console.error("Error updating service order status:", error);
      toast.error("Network error. Please try again.", {
        position: "bottom-center",
        autoClose: 3000,
        theme: "colored",
      });
    }
  };

  const handleSearch = (e) => {
    const query = e.target.value.toLowerCase();
    setSearchQuery(query);

    if (query === "" && statusFilter === "") {
      setFilteredData([]);
      setCurrentPage(1);
      return;
    }

    let filtered = serviceOrders;

    // Apply search filter
    if (query !== "") {
      filtered = filtered.filter(
        (order) =>
          order.order_number?.toLowerCase().includes(query) ||
          order.user?.name?.toLowerCase().includes(query) ||
          order.user?.email?.toLowerCase().includes(query) ||
          order.service?.title?.toLowerCase().includes(query) ||
          order.service?.user?.name?.toLowerCase().includes(query) ||
          order.id.toString().includes(query)
      );
    }

    // Apply status filter
    if (statusFilter !== "") {
      filtered = filtered.filter((order) => order.status === statusFilter);
    }

    setFilteredData(filtered);
    setCurrentPage(1);
  };

  const handleStatusFilter = (status) => {
    setStatusFilter(status);

    if (status === "" && searchQuery === "") {
      setFilteredData([]);
      setCurrentPage(1);
      return;
    }

    let filtered = serviceOrders;

    // Apply status filter
    if (status !== "") {
      filtered = filtered.filter((order) => order.status === status);
    }

    // Apply search filter
    if (searchQuery !== "") {
      filtered = filtered.filter(
        (order) =>
          order.order_number?.toLowerCase().includes(searchQuery) ||
          order.user?.name?.toLowerCase().includes(searchQuery) ||
          order.user?.email?.toLowerCase().includes(searchQuery) ||
          order.service?.title?.toLowerCase().includes(searchQuery) ||
          order.service?.user?.name?.toLowerCase().includes(searchQuery) ||
          order.id.toString().includes(searchQuery)
      );
    }

    setFilteredData(filtered);
    setCurrentPage(1);
  };

  const applyFilters = () => {
    setCurrentPage(1);
    setShowFiltersModal(false);
    fetchServiceOrders();
  };

  const clearFilters = () => {
    setDateFrom("");
    setDateTo("");
    setMinTotal("");
    setMaxTotal("");
    setStatusFilter("");
    setSearchQuery("");
    setCurrentPage(1);
  };

  // Get service order counts by status
  const getStatusCounts = () => {
    const counts = {
      all: serviceOrders.length,
      pending: serviceOrders.filter((o) => o.status === "pending").length,
      accepted: serviceOrders.filter((o) => o.status === "accepted").length,
      in_progress: serviceOrders.filter((o) => o.status === "in_progress")
        .length,
      completed: serviceOrders.filter((o) => o.status === "completed").length,
      cancelled: serviceOrders.filter((o) => o.status === "cancelled").length,
      refunded: serviceOrders.filter((o) => o.status === "refunded").length,
    };
    return counts;
  };

  const statusCounts = getStatusCounts();

  // Pagination logic
  const dataToShow = filteredData.length > 0 ? filteredData : serviceOrders;
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = dataToShow.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(dataToShow.length / itemsPerPage);

  const nextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const setPage = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPageNumbers = 10;
    const maxVisiblePages = Math.min(totalPages, maxPageNumbers);

    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage < maxVisiblePages - 1) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    return pageNumbers;
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid={true}>
          <Breadcrumbs title="Tafaseel Admin" breadcrumbItem="Service Orders" />

          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  <div id="serviceOrderList">
                    <Row className="g-4 mb-3">
                      <Col className="col-sm-auto">
                        <div className="d-flex gap-1">
                          <Button
                            color="primary"
                            className="refresh-btn"
                            onClick={fetchServiceOrders}
                            disabled={loading}
                          >
                            <i className="ri-refresh-line align-bottom me-1"></i>
                            {loading ? "Loading..." : "Refresh"}
                          </Button>
                          <Button
                            color="info"
                            className="filters-btn"
                            onClick={() => setShowFiltersModal(true)}
                          >
                            <i className="ri-filter-line align-bottom me-1"></i>
                            Advanced Filters
                          </Button>
                        </div>
                      </Col>
                      <Col className="col-sm">
                        <div className="d-flex justify-content-sm-end gap-2">
                          <div className="status-filter">
                            <select
                              className="form-select"
                              value={statusFilter}
                              onChange={(e) =>
                                handleStatusFilter(e.target.value)
                              }
                            >
                              {statusOptions.map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="search-box">
                            <input
                              type="text"
                              className="form-control search"
                              placeholder="Search service orders..."
                              value={searchQuery}
                              onChange={handleSearch}
                            />
                          </div>
                        </div>
                      </Col>
                    </Row>

                    {/* Status Summary Cards */}
                    <Row className="mb-3">
                      <Col>
                        <div className="d-flex gap-3 flex-wrap">
                          <div className="status-card p-2 border rounded">
                            <small className="text-muted">Total</small>
                            <div className="fw-bold">{statusCounts.all}</div>
                          </div>
                          <div className="status-card p-2 border rounded border-warning">
                            <small className="text-warning">Pending</small>
                            <div className="fw-bold text-warning">
                              {statusCounts.pending}
                            </div>
                          </div>
                          <div className="status-card p-2 border rounded border-info">
                            <small className="text-info">In Progress</small>
                            <div className="fw-bold text-info">
                              {statusCounts.in_progress}
                            </div>
                          </div>
                          <div className="status-card p-2 border rounded border-success">
                            <small className="text-success">Completed</small>
                            <div className="fw-bold text-success">
                              {statusCounts.completed}
                            </div>
                          </div>
                        </div>
                      </Col>
                    </Row>

                    <div className="table-responsive table-card mt-3 mb-1">
                      {loading ? (
                        <div className="text-center p-4">
                          <div
                            className="spinner-border text-primary"
                            role="status"
                          >
                            <span className="sr-only">Loading...</span>
                          </div>
                          <p className="mt-2">Loading service orders...</p>
                        </div>
                      ) : (
                        <table
                          className="table align-middle table-nowrap"
                          id="serviceOrderTable"
                        >
                          <thead className="table-light">
                            <tr>
                              <th className="sort" data-sort="order_id">
                                Order ID
                              </th>
                              <th className="sort" data-sort="customer">
                                Customer
                              </th>
                              <th className="sort" data-sort="service">
                                Service
                              </th>
                              <th className="sort" data-sort="provider">
                                Provider
                              </th>
                              <th className="sort" data-sort="total_amount">
                                Total Amount
                              </th>
                              <th className="sort" data-sort="delivery_date">
                                Delivery Date
                              </th>
                              <th className="sort" data-sort="status">
                                Status
                              </th>
                              <th className="sort" data-sort="created_at">
                                Created
                              </th>
                              <th className="sort" data-sort="action">
                                Action
                              </th>
                            </tr>
                          </thead>
                          <tbody className="list form-check-all">
                            {currentItems.length > 0 ? (
                              currentItems.map((serviceOrder) => (
                                <ServiceOrderTableItem
                                  key={serviceOrder.id}
                                  serviceOrder={serviceOrder}
                                  handleShow={handleShow}
                                  handleDelete={handleDelete}
                                  handleUpdateStatus={
                                    handleUpdateServiceOrderStatus
                                  }
                                />
                              ))
                            ) : (
                              <tr>
                                <td colSpan="9" className="text-center p-4">
                                  <div className="text-center">
                                    <lord-icon
                                      src="https://cdn.lordicon.com/msoeawqm.json"
                                      trigger="loop"
                                      colors="primary:#121331,secondary:#08a88a"
                                      style={{ width: "75px", height: "75px" }}
                                    ></lord-icon>
                                    <h5 className="mt-2">
                                      No Service Orders Found
                                    </h5>
                                    <p className="text-muted mb-0">
                                      {searchQuery || statusFilter
                                        ? `No service orders found matching your criteria`
                                        : "No service orders available at the moment."}
                                    </p>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      )}

                      {!loading &&
                        dataToShow.length === 0 &&
                        !searchQuery &&
                        !statusFilter && (
                          <div className="noresult text-center p-4">
                            <lord-icon
                              src="https://cdn.lordicon.com/msoeawqm.json"
                              trigger="loop"
                              colors="primary:#121331,secondary:#08a88a"
                              style={{ width: "75px", height: "75px" }}
                            ></lord-icon>
                            <h5 className="mt-2">
                              No Service Orders Available
                            </h5>
                            <p className="text-muted mb-0">
                              There are no service orders in the system yet.
                            </p>
                          </div>
                        )}
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                      <div className="d-flex justify-content-center">
                        <div className="pagination-wrap hstack gap-2">
                          <div className="d-flex justify-content-end">
                            <div
                              className="btn-group me-2"
                              role="group"
                              aria-label="Pagination"
                            >
                              <button
                                className="page-item pagination-prev btn btn-primary"
                                onClick={prevPage}
                                type="button"
                                disabled={currentPage === 1}
                              >
                                Previous
                              </button>

                              {getPageNumbers().map((pageNumber) => (
                                <button
                                  type="button"
                                  className={`btn ${
                                    pageNumber === currentPage
                                      ? "btn-primary"
                                      : "btn-outline-primary"
                                  }`}
                                  key={pageNumber}
                                  onClick={() => setPage(pageNumber)}
                                >
                                  {pageNumber}
                                </button>
                              ))}

                              <button
                                disabled={currentPage === totalPages}
                                className="page-item pagination-next btn btn-primary"
                                type="button"
                                onClick={nextPage}
                              >
                                Next
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Show results info */}
                    {dataToShow.length > 0 && (
                      <div className="d-flex justify-content-between align-items-center mt-3">
                        <div className="text-muted">
                          Showing {indexOfFirstItem + 1} to{" "}
                          {Math.min(indexOfLastItem, dataToShow.length)} of{" "}
                          {dataToShow.length} service orders
                        </div>
                        <div className="text-muted">
                          Page {currentPage} of {totalPages}
                        </div>
                      </div>
                    )}
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Service Order Details Modal */}
          {modal_center && (
            <ServiceOrderDetailsModal
              modal_center={modal_center}
              setmodal_center={setmodal_center}
              tog_center={tog_center}
              serviceOrder={selectedServiceOrder}
              onStatusUpdate={handleUpdateServiceOrderStatus}
            />
          )}

          {/* Advanced Filters Modal */}
          {showFiltersModal && (
            <div
              className="modal fade show d-block"
              tabIndex="-1"
              role="dialog"
            >
              <div
                className="modal-dialog modal-dialog-centered"
                role="document"
              >
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">Advanced Filters</h5>
                    <button
                      type="button"
                      className="btn-close"
                      onClick={() => setShowFiltersModal(false)}
                    ></button>
                  </div>
                  <div className="modal-body">
                    <Row>
                      <Col md={6}>
                        <div className="mb-3">
                          <label className="form-label">Date From</label>
                          <input
                            type="date"
                            className="form-control"
                            value={dateFrom}
                            onChange={(e) => setDateFrom(e.target.value)}
                          />
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="mb-3">
                          <label className="form-label">Date To</label>
                          <input
                            type="date"
                            className="form-control"
                            value={dateTo}
                            onChange={(e) => setDateTo(e.target.value)}
                          />
                        </div>
                      </Col>
                    </Row>
                    <Row>
                      <Col md={6}>
                        <div className="mb-3">
                          <label className="form-label">Min Total Amount</label>
                          <input
                            type="number"
                            className="form-control"
                            placeholder="Min Amount"
                            value={minTotal}
                            onChange={(e) => setMinTotal(e.target.value)}
                          />
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="mb-3">
                          <label className="form-label">Max Total Amount</label>
                          <input
                            type="number"
                            className="form-control"
                            placeholder="Max Amount"
                            value={maxTotal}
                            onChange={(e) => setMaxTotal(e.target.value)}
                          />
                        </div>
                      </Col>
                    </Row>
                  </div>
                  <div className="modal-footer">
                    <Button color="secondary" onClick={clearFilters}>
                      Clear All
                    </Button>
                    <Button color="primary" onClick={applyFilters}>
                      Apply Filters
                    </Button>
                  </div>
                </div>
              </div>
              <div className="modal-backdrop fade show"></div>
            </div>
          )}
        </Container>
      </div>
    </React.Fragment>
  );
};

export default ServiceOrders;
